import React from "react";
import PropTypes from "prop-types";
import "./Header.less";
import { setParam } from "../../redux/query";
import store from "../../redux";
import { getFhirSearchData, getResourceTypes } from "../../lib";
import { oktaSignOut } from "../../auth/oktaSignOut";

export default class FhirSearchHeader extends React.Component {
    static propTypes = {
        settings: PropTypes.object.isRequired,
        query: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        urlParams: PropTypes.object.isRequired,
        onSearchResults: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            resourceData: [],          // Metadata from API
            resourceTypes: [],         // List of resource types
            availableSearchParams: [], // Dropdown options for search parameters
            searchParams: [{ name: "", value: "" }], // Dynamic search rows
            selectedResourceType: "",
        };
    }

    componentDidMount() {
        this.fetchResourceTypes();
    }

    /** Fetch resource types for the first dropdown */
    fetchResourceTypes = async () => {
        try {
            const data = await getResourceTypes({
                url: this.props.settings.server.url + "/getMetaData",
                server: this.props.settings.server,
            });

            this.setState({
                resourceTypes: data?.rest?.[0]?.resource?.map(r => r.type) || [],
                resourceData: data?.rest?.[0]?.resource || [],
            });
        } catch (error) {
            console.error("Error fetching resource types:", error);
        }
    };

    /** Fetch search parameter options based on selected resource type */
    fetchSearchParameters = (resourceType) => {
        try {
            const params =
                this.state.resourceData.find(r => r.type === resourceType)?.searchParam?.map(p => p.name) || [];
            this.setState({ availableSearchParams: params });
        } catch (error) {
            console.error("Error fetching search parameters:", error);
        }
    };

    /** When resource type changes */
    handleResourceTypeChange = (e) => {
        const selectedType = e.target.value;
        this.setState({ selectedResourceType: selectedType, searchParams: [{ name: "", value: "" }] });

        store.dispatch(
            setParam({
                name: "resourceType",
                value: selectedType,
            })
        );

        if (selectedType) {
            this.fetchSearchParameters(selectedType);
        }
    };

    /** Handle search button click */
    handleSearch = async () => {
        const { selectedResourceType, searchParams } = this.state;

        if (!selectedResourceType) {
            alert("Please select a resource type before searching!");
            return;
        }

        try {
            // Notify parent search started
            if (this.props.onSearchResults) {
                this.props.onSearchResults(null, true, null);
            }

            const queryParams = searchParams
                .filter(p => p.name && p.value)
                .map(p => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.value)}`)
                .join("&");

            const encodedSearchParam = btoa(`${selectedResourceType}?${queryParams}`);
            const apiUrl = `${this.props.settings.server.url}/specialSearch?encodedInput=${encodedSearchParam}`;

            const data = await getFhirSearchData({ url: apiUrl, server: this.props.settings.server });

            if (this.props.onSearchResults) {
                this.props.onSearchResults(data, false, null);
            }
        } catch (error) {
            console.error("Search failed:", error);

            if (this.props.onSearchResults) {
                this.props.onSearchResults(null, false, error.message);
            }
        }
    };

    /** Remove a search parameter row */
    handleRemoveSearchParameter = (index) => {
        this.setState((prevState) => {
            const updatedParams = [...prevState.searchParams];
            updatedParams.splice(index, 1);
            return { searchParams: updatedParams };
        });
    };

    /** Update parameter name or value */
    handleSearchParamChange = (index, field, value) => {
        const updatedParams = [...this.state.searchParams];
        updatedParams[index][field] = value;
        this.setState({ searchParams: updatedParams });
    };

    /** Add a new search parameter row */
    handleAddSearchParameter = () => {
        this.setState((prevState) => ({
            searchParams: [...prevState.searchParams, { name: "", value: "" }],
        }));
    };

    /** Logout */
    handleLogout = () => {
        oktaSignOut();
    };

    /** Render the search form */
    renderFhirSearchTabContents() {
        const { resourceTypes, searchParams, availableSearchParams, selectedResourceType } = this.state;

        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    this.handleSearch();
                }}
            >
                {/* Resource Type Dropdown */}
                <div className="row">
                    <div className="col-sm-12">
                        <div className="form-group">
                            <div className="input-group">
                                <span className="input-group-addon">
                                    <small>Resource Type:</small>
                                </span>
                                <select
                                    className="form-control input-sm"
                                    value={selectedResourceType}
                                    onChange={this.handleResourceTypeChange}
                                    disabled={resourceTypes.length === 0}
                                >
                                    <option value="">Select Resource Type</option>
                                    {resourceTypes.map((type, index) => (
                                        <option key={index} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dynamic Search Parameters */}
                {selectedResourceType && (
                    <div className="row search-params-section">
                        {searchParams.map((param, index) => (
                            <div className="col-sm-6 search-parameter-col" key={index}>
                                <div className="form-group search-param-name">
                                    <label>Search Parameter:</label>
                                    <select
                                        className="form-control input-sm"
                                        value={param.name}
                                        onChange={(e) =>
                                            this.handleSearchParamChange(index, "name", e.target.value)
                                        }
                                    >
                                        <option value="">Select Parameter</option>
                                        {availableSearchParams.map((name, i) => (
                                            <option key={i} value={name}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group search-param-value">
                                    <label>Value:</label>
                                    <input
                                        type="text"
                                        className="form-control input-sm"
                                        value={param.value}
                                        onChange={(e) =>
                                            this.handleSearchParamChange(index, "value", e.target.value)
                                        }
                                        placeholder="Enter value"
                                    />
                                </div>

                                {/* Remove Button */}
                                <div className="remove-param-btn">
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => this.handleRemoveSearchParameter(index)}
                                        style={{ marginTop: "1rem" }}
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </form>
        );
    }

    render() {
        return (
            <>
                <div className="tab-content">
                    <div className="tab-pane active">
                        {this.renderFhirSearchTabContents()}
                    </div>

                    <div className="text-right" style={{ height: 0 }}>
                        <button
                            type="button"
                            onClick={this.handleSearch}
                            className="btn btn-primary btn-submit"
                        >
                            <i className="fa fa-search" /> Search
                        </button>
                    </div>
                </div>

                <div className="sort-widget small">
                    {this.state.selectedResourceType && (
                        <span
                            className="pull-left search-param-btn"
                            onClick={this.handleAddSearchParameter}
                            style={{ cursor: "pointer" }}
                        >
                            Add Search Parameter
                        </span>
                    )}
                </div>
            </>
        );
    }
}
